// Admin Console API client.
//
// Hits the legacy api/ Cloud Run service at /api/admin/* (Netlify proxies
// to BACKEND_URL in prod; Vite proxies to :8000 in dev). Authorization is
// the same PyJWT Bearer token the rest of web2 uses.
//
// Mirrors web/scripts/admin.js's adminFetch / adminPost / adminPut /
// adminDelete helpers — same Authorization + X-Source headers, same
// URL contracts. Types come from inspection of admin.js's renderers.

const BASE = "/api/admin";

// ---------------------------------------------------------------------------
// Auth-aware request helpers
// ---------------------------------------------------------------------------

function authHeaders(bearer: string): HeadersInit {
  return {
    Authorization: `Bearer ${bearer}`,
    "X-Source": "web",
  };
}

function jsonHeaders(bearer: string): HeadersInit {
  return {
    ...authHeaders(bearer),
    "Content-Type": "application/json",
  };
}

function qs(params: object | undefined): string {
  if (!params) return "";
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

async function get<T>(bearer: string, path: string, params?: object): Promise<T> {
  const r = await fetch(`${BASE}${path}${qs(params)}`, { headers: authHeaders(bearer) });
  if (!r.ok) throw await toError(r);
  return (await r.json()) as T;
}

async function post<T>(bearer: string, path: string, body: unknown): Promise<T> {
  const r = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: jsonHeaders(bearer),
    body: JSON.stringify(body),
  });
  if (!r.ok) throw await toError(r);
  return (await r.json()) as T;
}

async function put<T>(bearer: string, path: string, body: unknown): Promise<T> {
  const r = await fetch(`${BASE}${path}`, {
    method: "PUT",
    headers: jsonHeaders(bearer),
    body: JSON.stringify(body),
  });
  if (!r.ok) throw await toError(r);
  return (await r.json()) as T;
}

async function del(bearer: string, path: string): Promise<void> {
  const r = await fetch(`${BASE}${path}`, { method: "DELETE", headers: authHeaders(bearer) });
  if (!r.ok) throw await toError(r);
}

async function toError(r: Response): Promise<Error> {
  let detail = "";
  try {
    const data = await r.json();
    detail = data?.detail || data?.message || JSON.stringify(data);
  } catch {
    detail = await r.text().catch(() => "");
  }
  const err = new Error(`HTTP ${r.status}${detail ? `: ${detail}` : ""}`);
  (err as Error & { status?: number }).status = r.status;
  return err;
}

// ---------------------------------------------------------------------------
// Types — mirror the response shapes admin.js renders
// ---------------------------------------------------------------------------

export interface OverviewResponse {
  queries?: { today?: number; last_7d?: number; last_30d?: number };
  active_users_7d?: number;
  cache_hit_rate_30d?: number;
  avg_response_time_ms_30d?: number;
  query_volume_by_day?: Array<{ day: string; count: number }>;

  activity_kpis?: {
    events_today?: number;
    events_7d?: number;
    unique_users_7d?: number;
    top_actions_7d?: Array<{ action: string; count: number }>;
  };

  sessions_kpis?: {
    total_30d?: number;
    avg_queries_per_session?: number;
    error_rate_30d?: number;
    top_datasources_30d?: Array<{ datasource_name: string; count: number }>;
  };

  users_kpis?: {
    total_users?: number;
    active_7d?: number;
    avg_sessions_per_user_30d?: number;
    top_users_by_sessions?: Array<{ user_email: string; session_count: number }>;
  };

  datasources_kpis?: {
    total_datasources?: number;
    total_tables?: number;
    org_count?: number;
    active_datasources_30d?: number;
  };

  quality_kpis?: { avg_overall_30d?: number };
  cost_kpis?: { avg_cost_per_query_30d?: number; avg_tokens_per_query_30d?: number };
}

export interface ActivityRow {
  id?: number;
  created_at: string;
  user_email: string;
  organization: string;
  source: string;
  action: string;
  trace_id?: string | null;
  details?: Record<string, unknown> | null;
}

export interface ActivityResponse {
  items: ActivityRow[];
  total?: number;
}

export interface SessionRow {
  session_id: string;
  user_email: string;
  organization: string;
  source: string;
  started_at: string;
  last_activity_at: string;
  query_count: number;
  error_count: number;
  cache_hits?: number;
  avg_time_ms?: number | null;
  avg_quality_score?: number | null;
  total_tokens?: number | null;
  total_cost_usd?: number | null;
  datasource_name?: string;
  thread_id?: number | null;
}

export interface SessionsResponse {
  items: SessionRow[];
  total: number;
}

export interface SessionQueryEvent {
  type: "query";
  timestamp: string;
  query_id: string;
  raw_query: string;
  agent_sub_query?: string | null;
  status?: string | null;
  execution_time_ms?: number | null;
  datasource_name?: string | null;
  overall_score?: number | null;
  sql_generated?: string | null;
  trace_id?: string | null;
  cache_hit?: boolean | null;
  error_message?: string | null;
  source?: string | null;
  thread_id?: number | null;
  message_id?: number | null;
}

export interface SessionEventsResponse {
  session_id: string;
  user_email?: string | null;
  organization?: string | null;
  source?: string | null;
  events: SessionQueryEvent[];
  audit_trail?: unknown[];
}

export interface UserRow {
  user_email: string;
  organization?: string | null;
  session_count: number;
  total_queries: number;
  last_active?: string | null;
  feedback_given?: number;
}

export interface AuthorizedUserOrgMembership {
  name: string;
  is_default?: boolean;
}

export interface AuthorizedUserRow {
  email: string;
  name?: string | null;
  role: string;
  status: string;
  organizations: AuthorizedUserOrgMembership[];
  org_role?: string | null;
  org_dept_name?: string | null;
}

export interface CreateUserRequest {
  email: string;
  name?: string;
  role: string;
  organizations: string[];
  default_org: string;
  org_role?: string;
  org_dept_name?: string;
  create_org?: boolean;
}

export interface UpdateUserRequest {
  name?: string;
  role?: string;
  org_role?: string;
  org_dept_name?: string;
}

export interface DatasourceRow {
  organization: string;
  datasource_name: string;
  storage_type: string;
  table_count: number;
}

// ---------------------------------------------------------------------------
// Endpoint surface
// ---------------------------------------------------------------------------

export interface OrgScope {
  /** Empty string = all orgs (Super Admin). */
  organization?: string;
}

export const adminApi = {
  overview(bearer: string, scope: OrgScope): Promise<OverviewResponse> {
    return get(bearer, "/overview", scope);
  },

  organizations(bearer: string, hasPublishedModel = true): Promise<string[]> {
    return get(bearer, "/organizations", { has_published_model: hasPublishedModel });
  },

  activity(
    bearer: string,
    params: OrgScope & { action?: string; page?: number; page_size?: number },
  ): Promise<ActivityResponse> {
    return get(bearer, "/activity", params);
  },

  sessions(
    bearer: string,
    params: OrgScope & { source?: string; page?: number; page_size?: number },
  ): Promise<SessionsResponse> {
    return get(bearer, "/sessions", params);
  },

  sessionEvents(
    bearer: string,
    sessionId: string,
    scope: OrgScope,
  ): Promise<SessionEventsResponse> {
    return get(bearer, `/sessions/${encodeURIComponent(sessionId)}/events`, scope);
  },

  users(bearer: string, scope: OrgScope): Promise<UserRow[]> {
    return get(bearer, "/users", scope);
  },

  userAccess(
    bearer: string,
    params: OrgScope & { include_inactive?: boolean | string },
  ): Promise<AuthorizedUserRow[]> {
    return get(bearer, "/users/access", params);
  },

  createUser(bearer: string, body: CreateUserRequest): Promise<AuthorizedUserRow> {
    return post(bearer, "/users", body);
  },

  updateUser(bearer: string, email: string, body: UpdateUserRequest): Promise<AuthorizedUserRow> {
    return put(bearer, `/users/${encodeURIComponent(email)}`, body);
  },

  setUserStatus(bearer: string, email: string, status: "active" | "inactive"): Promise<void> {
    return put(bearer, `/users/${encodeURIComponent(email)}/status`, { status }).then(() => undefined);
  },

  deleteUser(bearer: string, email: string): Promise<void> {
    return del(bearer, `/users/${encodeURIComponent(email)}`);
  },

  addUserOrg(
    bearer: string,
    email: string,
    body: { organization: string; is_default: boolean },
  ): Promise<unknown> {
    return post(bearer, `/users/${encodeURIComponent(email)}/organizations`, body);
  },

  removeUserOrg(bearer: string, email: string, org: string): Promise<void> {
    return del(
      bearer,
      `/users/${encodeURIComponent(email)}/organizations/${encodeURIComponent(org)}`,
    );
  },

  setUserDefaultOrg(bearer: string, email: string, org: string): Promise<unknown> {
    return put(
      bearer,
      `/users/${encodeURIComponent(email)}/organizations/${encodeURIComponent(org)}/default`,
      {},
    );
  },

  datasources(bearer: string, scope: OrgScope): Promise<DatasourceRow[]> {
    return get(bearer, "/datasources", scope);
  },
};
